import { createElement as rc, Fragment } from 'react';
import format from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import lodash from 'lodash';
const { get } = lodash;
import { Text, styled, webOnlyStyles, fromTheme, View, ExternalLink } from 'lib_ui-primitives';
import { nativeOnlyProperties, valueUtilities, rfidStrategy } from 'lib_ui-services';
import ButtonColumn from './ButtonColumn';
import { getComponent } from './componentRegistry';
import LinkColumn from './LinkColumn';
import getNestedDetailValue from './getNestedDetailValue';
import getNestedPatchDetailColumnHNodes from './PatchDetail/getNestedPatchDetailColumnHNodes';

let ColumnWidth = styled(Text).attrs(props => ({
    title: typeof props.children === 'string' ? props.children : undefined,
    name: 'column',
    role: 'gridcell',
    ...nativeOnlyProperties({ numberOfLines: 2, ellipsizeMode: 'tail' })
}))`
    width: ${({ width }) => width + 'px'};
    border-left-width: 0px;
    border-left-color: ${fromTheme('gridLineColor')};
    text-align: left;
    overflow: hidden;
    padding-left: ${fromTheme('textPadding')};
    padding-right: ${fromTheme('textPadding')};
`;
let ColumnNoWidth = styled(Text).attrs(props => ({
    title: typeof props.children === 'string' ? props.children : undefined,
    name: 'column',
    role: 'gridcell',
    ...nativeOnlyProperties({ numberOfLines: props.$autoHeight ? 0 : 2, ellipsizeMode: 'tail' })
}))`
    border-left-width: 0px;
    border-left-color: ${fromTheme('gridLineColor')};
    text-align: left;
    overflow: hidden;
    padding-left: ${fromTheme('textPadding')};
    padding-right: ${fromTheme('textPadding')};
`;

ColumnNoWidth = webOnlyStyles(ColumnNoWidth)`
    border-left-style: solid;
    white-space: ${({ $wrapText }) => $wrapText ? 'normal' : 'nowrap'};
    overflow-wrap: ${({ $wrapText }) => $wrapText ? 'anywhere' : 'normal'};
    text-overflow: ellipsis;
`;

ColumnWidth = webOnlyStyles(ColumnWidth)`
    border-left-style: solid;
    white-space: ${({ $wrapText }) => $wrapText ? 'normal' : 'nowrap'};
    overflow-wrap: ${({ $wrapText }) => $wrapText ? 'anywhere' : 'normal'};
    text-overflow: ellipsis;
`;

const IconColumn = styled(View).attrs({ name: 'icon-column' })`
    width: ${({ width }) => width + 'px'};
    margin-left: -6px;
    margin-right: 6px;
    flex-direction: column;
`;
IconColumn.displayName = 'IconColumn';

const doNotPropagate = e => {
    // e.preventDefault?.();
    e.stopPropagation?.();
};

/**
 * Returns a React component representing a cell in a column based on the provided properties.
 *
 * @param {Object} params - Parameters for the cell.
 * @param {Object} params.record - The data record for the cell.
 * @param {Object} params.columnHNode - Configuration object for the column.
 * @param {Object} [params.style] - Style object for custom styling.
 * @param {boolean} [params.skipUndefined=false] - Whether to skip rendering if the value is undefined.
 * @param {Function} [params.platformSpecificValueLookup] - Function to look up platform-specific values.
 * @param {number} [params.width] - Width of the column.
 * @param {boolean} [params.setWidth] - Whether to set the width of the column.
 * @param {Array} [params.featureFlags] - Feature flags for conditional rendering.
 * @param {Object} [params.activeRecord] - Active record data.
 * @param {Object} [params.splashRecord] - Splash record data.
 * @param {Object} [params.navigationSelection] - Navigation selection data.
 * @param {string} [params.key] - Key for the cell.
 * @param {React.Component} [params.PatchDetailTableComponent] - Patch detail table component.
 * @returns {React.Component|null} - A React component representing the cell or null if conditions are not met.
 */
export default function getColumnCell({
    record,
    columnHNode,
    style,
    skipUndefined = false,
    platformSpecificValueLookup,
    width,
    setWidth,
    featureFlags,
    activeRecord,
    splashRecord,
    navigationSelection,
    key
}) {
    if (!columnHNode || !record) return;

    if (columnHNode.dataType === 'button') {
        return rc(ButtonColumn, { width, hNode: columnHNode, record, key });
    }

    let Column;
    if (setWidth) {
        Column = ColumnWidth;
    } else {
        Column = ColumnNoWidth;
    }

    let columnLabel = columnHNode.children?.length ? columnHNode.children[0].label : columnHNode.label;
    const testAttrs = {
        'data-testid': `column-${columnHNode.propertyName}`,
        'data-columnlabel': columnLabel,
        $autoHeight: columnHNode.autoHeight
    };

    let value = get(
        record,
        columnHNode.propertyPath ? `${columnHNode.propertyPath}.${columnHNode.propertyName}` : columnHNode.propertyName
    );
    if (columnHNode.displayProperties?.length) {
        let prefix = [columnHNode.propertyPath, columnHNode.namespace, columnHNode.relation].filter(Boolean).join(':');
        if (prefix) prefix += '.';

        value = columnHNode.displayProperties
            .map(property => get(record, `${prefix}${property}`))
            .filter(Boolean)
            .join(', ');
    }

    if (skipUndefined && typeof value === 'undefined') return;
    if (!value) {
        if (columnHNode.flagOnValue === 'true' || columnHNode.flagOnValue === 'false') {
            value = 'false';
        }
    }

    // Some display values must be rendered by components that are platform specific
    // (such as images (base64 column.dataType)).
    if (platformSpecificValueLookup) {
        // TODO: instead of platformSpecificValueLookup use .native/.browser or whatever
        throw new Error('not implemented');
        // let platformValue = platformSpecificValueLookup(column.dataType, value);
        // if (typeof platformValue !== 'undefined') return platformValue;
    }
    if (value == null && columnHNode.dataType !== 'mappedBoolean') {
        return rc(Column, { width, ...testAttrs, key }, '');
    }
    // Most datatypes can just be converted to some string format.
    switch (columnHNode.dataType) {
        case 'text':
            if (typeof value === 'number') {
                value = value.toString();
            }
            if (typeof value === 'boolean') {
                value = value.toString();
            }
            if (typeof value !== 'string') {
                throw new Error(
                    `A text column cell for ${columnHNode.propertyName} was passed ${typeof value} instead of text.`
                );
            }
            if (Array.isArray(value)) {
                value = value.join(', ');
            }
            return rc(Column, { width, style, $wrapText: columnHNode.wrapText, ...testAttrs, key }, value);
        // Rendered through the same Column as a text cell, deliberately.
        //
        // This drew its own progress bar: a styled(View) wrapping a bare Text. Two things were
        // wrong with that. The Text set no colour, and Column is a styled(Text) — so a fraction
        // cell inherited none of the row's font colour, padding, ellipsis or selection styling,
        // and the numbers were unreadable on a highlighted row. And the bar's colours came from
        // `progressBarColor` and `gridLineColor`, which a comment here claimed were defined in
        // both themes. They are not: in gl2025 `progressBarColor` is nested inside a component
        // scope and `gridLineColor` exists only under `charts`, so neither resolves at the top
        // level and there was no bar to justify the cost.
        //
        // The ticket asks for the fraction to be displayed ("32 / 50 Verified"); the bar was an
        // embellishment. Showing the numbers the way every other column shows text is legible in
        // both themes, in dark mode, and on a selected row, and it depends on no token at all.
        case 'fraction': {
            // getColumnValue already returns '' for a null/undefined value, so this is the
            // stored "32 / 50" string in every real case; the guard is for a mis-bound column.
            const fraction = value == null ? '' : String(value);
            return rc(Column, { width, style, ...testAttrs, key }, fraction);
        }
        case 'dateTime':
            if (columnHNode.format === 'ISO') return rc(Column, { width, style, key }, formatISO(new Date(value)));
            return rc(Column, { width, style, ...testAttrs, key }, formatDate(value, columnHNode.format));
        case 'localShortDate':
            return rc(Column, { width, style, ...testAttrs, key }, formatDate(value, 'P'));
        case 'localVerboseDateTime':
            return rc(Column, { width, style, ...testAttrs, key }, formatDate(value, 'PP p'));
        case 'encodedText':
            return rc(Column, { width, style, ...testAttrs, key }, rfidStrategy.getDisplayValue(value));
        case 'mappedBoolean':
            if (value == null) {
                return rc(Column, { width, style, ...testAttrs, key }, '');
            } else if (columnHNode.mappedTrueValue || columnHNode.mappedFalseValue) {
                return rc(
                    Column,
                    { width, style, ...testAttrs, key },
                    value ? columnHNode.mappedTrueValue || '' : columnHNode.mappedFalseValue || ''
                );
            } else {
                return rc(Column, { width, style, ...testAttrs, key }, value ? 'True' : 'False');
            }
        case 'gpsCoordinates': {
            const url = getMapsUrl(value);
            if (!url) return rc(Column, { width, style, ...testAttrs, key }, '');
            return rc(
                IconColumn,
                { width, style, ...testAttrs, key, onClick: doNotPropagate },
                rc(ExternalLink, { url, iconName: columnHNode.iconName, iconSize: '32px' })
            );
        }
        case 'nestedDetail': {
            let text = getNestedDetailValue(value, columnHNode, {
                featureFlags,
                activeRecord,
                splashRecord,
                navigationSelection
            });
            if (typeof text === 'number') {
                text = text.toString();
            }
            if (typeof text !== 'string') {
                throw new Error(
                    `A nested detail column cell for ${columnHNode.propertyName} contained something other than text.`
                );
            }
            return rc(Column, { width, style, ...testAttrs, key }, text);
        }
        case 'patchDetail': {
            if (columnHNode.asCardBody) {
                // Use the component registry to get PatchDetailTable
                const PatchDetailTable = getComponent('PatchDetailTable');
                return rc(PatchDetailTable, {
                    parentRecordId: record._id,
                    patches: value,
                    width,
                    style,
                    hNode: columnHNode,
                    ...testAttrs,
                    key
                });
            }
            const subColumns = getNestedPatchDetailColumnHNodes();
            return rc(
                Fragment,
                null,
                subColumns.map(subColumn =>
                    getColumnCell({
                        record: value[0],
                        columnHNode: subColumn,
                        style,
                        skipUndefined,
                        platformSpecificValueLookup,
                        //theme,
                        width: subColumn.width,
                        setWidth,
                        featureFlags,
                        activeRecord,
                        splashRecord,
                        navigationSelection,
                        key: `${key || record._id}-${subColumn.id}`
                    })
                )
            );
        }
        case 'currency':
            return rc(Column, { width, style, ...testAttrs, key }, valueUtilities.formatCurrency(value));
        case 'link': {
            return rc(LinkColumn, { record, width, style, hNode: columnHNode, Column, ...testAttrs, key });
        }
        default:
            throw new Error(`Unknown data type for column ${columnHNode.label}`);
    }
}

function getMapsUrl(value) {
    /* URL parameters:
        q=latN+lonW+(label)     location of teardrop
        t=k             keyhole (satellite map)
        t=h             hybrid
        ll=lat,-lon     center of map
        spn=w.w,h.h     span of map, degrees ex: &spn=0.004250,0.011579
        hl=en           language
    */
    const latitude = value.firstCoordinate ?? value.y;
    const longitude = value.secondCoordinate ?? value.x;
    if (latitude == null || longitude == null || latitude === '' || longitude === '') return;
    // this format no longer seems to work:
    // &iwloc=A     something to do with info window
    // return `http://maps.google.com/maps?q=${latitude}+${longitude}+(label)&ll=${latitude},${longitude}&t=h&hl=en&z=19`;

    // this works:
    return `https://www.google.com/maps/place/${latitude},${longitude}/@${latitude},${longitude},1453m`;

    // another option: open the directions straight away
    // return `https://www.google.com/maps/dir//${latitude},${longitude}/@${latitude},${longitude},1453m/`;
}

function formatDate(value, desiredFormat) {
    return value == '' || value == null ? '' : format(new Date(value), desiredFormat);
}
